import React, { useEffect, useState, useRef } from 'react';
import { Wallet, Plus, History, CreditCard, TrendingUp, Gift, Star, Clock, RefreshCcw, Calendar, Award, Lock, ChevronRight, ChevronDown, ChevronsUp, BarChart2, ArrowRight, ArrowLeftRight, Shield, AlertTriangle, Eye, EyeOff, MessageCircle, Heart, Send, Download, Upload, Search, Filter, Trash } from 'lucide-react';
import { TokenStore } from './TokenStore';
interface AdvancedTokenSystemProps {
  onNavigate?: (section: string) => void;
}
export const AdvancedTokenSystem: React.FC<AdvancedTokenSystemProps> = ({
  onNavigate
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showWalletBalance, setShowWalletBalance] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('luv');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showTokenStore, setShowTokenStore] = useState(false);
  const [tokenType, setTokenType] = useState<'luv' | 'gems' | null>(null);
  const tokenSystemRef = useRef<HTMLDivElement>(null);
  const toggleWalletBalance = () => {
    setShowWalletBalance(!showWalletBalance);
  };
  // Handle opening the token store
  const handleOpenTokenStore = (type: 'luv' | 'gems') => {
    setTokenType(type);
    setShowTokenStore(true);
  };
  // Handle purchase in token store
  const handlePurchase = (bundleId: string) => {
    // Here you would implement the actual purchase logic
    console.log(`Purchased bundle: ${bundleId}`);
    // Close the token store
    setShowTokenStore(false);
  };
  // Handle clicks outside the token system
  const handleOutsideClick = (e: MouseEvent) => {
    if (tokenSystemRef.current && !tokenSystemRef.current.contains(e.target as Node)) {
      // If clicked outside the token system, navigate back to profile
      if (onNavigate) {
        onNavigate('profile');
      }
    }
  };
  useEffect(() => {
    // Add event listener when component mounts
    document.addEventListener('mousedown', handleOutsideClick);
    // Remove event listener when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onNavigate]);
  // If token store is open, show it
  if (showTokenStore) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-161">
        <div className="w-full max-w-2xl max-h-[90vh] overflow-auto bg-white dark:bg-black rounded-lg shadow-2xl" data-id="element-162">
          <TokenStore onBack={() => setShowTokenStore(false)} onPurchase={handlePurchase} currentTokens={240} data-id="element-163" />
        </div>
      </div>;
  }
  return <section className="w-full" data-id="element-164">
      {/* Main content container with ref for detecting outside clicks */}
      <div className="max-w-[860px] mx-auto px-4 md:max-w-[860px] lg:max-w-[1080px]" data-id="element-165">
        <div ref={tokenSystemRef} className="w-full mx-auto bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 space-y-6" data-id="element-166">
          <div className="flex items-center justify-between" data-id="element-167">
            <h2 className="text-lg font-bold" data-id="element-168">Advanced Token System</h2>
            <button className="text-sm text-pink-500 dark:text-pink-400 flex items-center gap-1" data-id="element-169">
              <History size={14} data-id="element-170" />
              <span data-id="element-171">Transaction History</span>
            </button>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-purple-600/5 transition-all duration-300" data-id="element-172">
            <div className="overflow-x-auto" data-id="element-173">
              <div className="flex p-2 gap-2" data-id="element-174">
                <button className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'overview' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-175">
                  <BarChart2 size={14} data-id="element-176" />
                  <span data-id="element-177">Overview</span>
                </button>
                <button className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'wallet' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`} onClick={() => setActiveTab('wallet')} data-id="element-178">
                  <Wallet size={14} data-id="element-179" />
                  <span data-id="element-180">Wallet</span>
                </button>
                <button className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'staking' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`} onClick={() => setActiveTab('staking')} data-id="element-181">
                  <TrendingUp size={14} data-id="element-182" />
                  <span data-id="element-183">Staking</span>
                </button>
                <button className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'subscription' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`} onClick={() => setActiveTab('subscription')} data-id="element-184">
                  <RefreshCcw size={14} data-id="element-185" />
                  <span data-id="element-186">Subscription</span>
                </button>
                <button className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'rewards' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`} onClick={() => setActiveTab('rewards')} data-id="element-187">
                  <Award size={14} data-id="element-188" />
                  <span data-id="element-189">Rewards</span>
                </button>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-190">
              {activeTab === 'overview' && <div className="space-y-4" data-id="element-191">
                  {/* Token Balance */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-192">
                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/30 rounded-xl border border-pink-200 dark:border-pink-800/50 p-4 shadow-sm hover:shadow-lg hover:shadow-pink-600/10 transition-all duration-300" data-id="element-193">
                      <div className="flex items-center gap-2 mb-2" data-id="element-194">
                        <span className="text-pink-500 dark:text-pink-400 text-xl" data-id="element-195">
                          💗
                        </span>
                        <h3 className="font-medium dark:text-white" data-id="element-196">
                          LuvTokens (XP)
                        </h3>
                      </div>
                      <p className="text-2xl font-bold text-gray-800 dark:text-white" data-id="element-197">
                        240
                      </p>
                      <div className="mt-3 flex items-center justify-between" data-id="element-198">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-199">
                          Experience currency
                        </p>
                        <button className="flex items-center gap-1 px-2 py-1 bg-pink-500 dark:bg-pink-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-pink-500/20 transition-all duration-300" onClick={() => handleOpenTokenStore('luv')} data-id="element-200">
                          <Plus size={12} data-id="element-201" />
                          <span data-id="element-202">Boost XP</span>
                        </button>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30 rounded-xl border border-purple-200 dark:border-purple-800/50 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/10 transition-all duration-300" data-id="element-203">
                      <div className="flex items-center gap-2 mb-2" data-id="element-204">
                        <span className="text-purple-500 dark:text-purple-400 text-xl" data-id="element-205">
                          💎
                        </span>
                        <h3 className="font-medium dark:text-white" data-id="element-206">
                          DesireGems
                        </h3>
                      </div>
                      <p className="text-2xl font-bold text-gray-800 dark:text-white" data-id="element-207">
                        15
                      </p>
                      <div className="mt-3 flex items-center justify-between" data-id="element-208">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-209">
                          Premium currency
                        </p>
                        <button className="flex items-center gap-1 px-2 py-1 bg-purple-500 dark:bg-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-500/20 transition-all duration-300" onClick={() => handleOpenTokenStore('gems')} data-id="element-210">
                          <Plus size={12} data-id="element-211" />
                          <span data-id="element-212">Get More</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Token Analytics */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-213">
                    <div className="flex items-center justify-between mb-3" data-id="element-214">
                      <h3 className="font-medium dark:text-white" data-id="element-215">
                        Token Analytics
                      </h3>
                      <div className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300" data-id="element-216">
                        Last 30 days
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-217">
                      <div className="space-y-1" data-id="element-218">
                        <div className="flex items-center justify-between" data-id="element-219">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-220">
                            Earned
                          </span>
                          <span className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-221">
                            +120 💗
                          </span>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-222">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-223">
                            Spent
                          </span>
                          <span className="text-sm font-medium text-red-500 dark:text-red-400" data-id="element-224">
                            -85 💗
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-1 border-t border-gray-200 dark:border-gray-700" data-id="element-225">
                          <span className="text-xs dark:text-gray-300" data-id="element-226">
                            Net Change
                          </span>
                          <span className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-227">
                            +35 💗
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1" data-id="element-228">
                        <div className="flex items-center justify-between" data-id="element-229">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-230">
                            Earned
                          </span>
                          <span className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-231">
                            +8 💎
                          </span>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-232">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-233">
                            Spent
                          </span>
                          <span className="text-sm font-medium text-red-500 dark:text-red-400" data-id="element-234">
                            -12 💎
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-1 border-t border-gray-200 dark:border-gray-700" data-id="element-235">
                          <span className="text-xs dark:text-gray-300" data-id="element-236">
                            Net Change
                          </span>
                          <span className="text-sm font-medium text-red-500 dark:text-red-400" data-id="element-237">
                            -4 💎
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="h-24 flex items-end gap-1" data-id="element-238">
                      {[30, 45, 20, 60, 75, 50, 90].map((height, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-239">
                          <div className="w-full bg-gradient-to-t from-pink-600 to-purple-600 rounded-t-sm" style={{
                      height: `${height}%`
                    }} data-id="element-240"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-241">
                            {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                          </span>
                        </div>)}
                    </div>
                  </div>
                  {/* Token Packages */}
                  <div data-id="element-242">
                    <div className="flex items-center justify-between mb-3" data-id="element-243">
                      <h3 className="font-medium dark:text-white" data-id="element-244">
                        Token Packages
                      </h3>
                      <button className="text-xs text-pink-500 dark:text-pink-400" onClick={() => handleOpenTokenStore('luv')} data-id="element-245">
                        See All
                      </button>
                    </div>
                    <div className="space-y-2" data-id="element-246">
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex justify-between items-center shadow-sm hover:shadow-md hover:shadow-pink-600/5 transition-all duration-300" data-id="element-247">
                        <div className="flex items-center gap-3" data-id="element-248">
                          <div className="w-10 h-10 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-249">
                            <span className="text-sm font-bold" data-id="element-250">100</span>
                          </div>
                          <div data-id="element-251">
                            <p className="text-sm font-medium dark:text-white" data-id="element-252">
                              Starter Flirt Pack
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-253">
                              100 💗
                            </p>
                          </div>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-pink-600/20 transition-all duration-300" onClick={() => handleOpenTokenStore('luv')} data-id="element-254">
                          $9.99
                        </button>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex justify-between items-center shadow-sm hover:shadow-md hover:shadow-pink-600/5 transition-all duration-300" data-id="element-255">
                        <div className="flex items-center gap-3" data-id="element-256">
                          <div className="w-10 h-10 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-257">
                            <span className="text-sm font-bold" data-id="element-258">400</span>
                          </div>
                          <div data-id="element-259">
                            <div className="flex items-center gap-1" data-id="element-260">
                              <p className="text-sm font-medium dark:text-white" data-id="element-261">
                                Flash Sale Bundle
                              </p>
                              <div className="text-xs px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-262">
                                25% OFF
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-263">
                              400 💗 + 100 Bonus
                            </p>
                          </div>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-pink-600/20 transition-all duration-300" onClick={() => handleOpenTokenStore('luv')} data-id="element-264">
                          $29.99
                        </button>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex justify-between items-center shadow-sm hover:shadow-md hover:shadow-pink-600/5 transition-all duration-300" data-id="element-265">
                        <div className="flex items-center gap-3" data-id="element-266">
                          <div className="w-10 h-10 flex items-center justify-center bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-500 dark:text-purple-400" data-id="element-267">
                            <span className="text-sm font-bold" data-id="element-268">50</span>
                          </div>
                          <div data-id="element-269">
                            <p className="text-sm font-medium dark:text-white" data-id="element-270">
                              Premium Gems
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-271">
                              50 💎
                            </p>
                          </div>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-pink-600/20 transition-all duration-300" onClick={() => handleOpenTokenStore('gems')} data-id="element-272">
                          $24.99
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'wallet' && <div className="space-y-4" data-id="element-273">
                  {/* Wallet Header with Balance */}
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl border border-pink-200 dark:border-pink-800/30 p-4 shadow-sm hover:shadow-lg hover:shadow-pink-600/10 transition-all duration-300" data-id="element-274">
                    <div className="flex items-center justify-between mb-3" data-id="element-275">
                      <div className="flex items-center gap-2" data-id="element-276">
                        <Wallet size={18} className="text-pink-500 dark:text-pink-400" data-id="element-277" />
                        <h3 className="font-medium dark:text-white" data-id="element-278">
                          Token Wallet
                        </h3>
                      </div>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" onClick={toggleWalletBalance} data-id="element-279">
                        {showWalletBalance ? <Eye size={16} className="text-gray-500 dark:text-gray-400" data-id="element-280" /> : <EyeOff size={16} className="text-gray-500 dark:text-gray-400" data-id="element-281" />}
                      </button>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-282">
                      <div className="flex items-center justify-between mb-2" data-id="element-283">
                        <span className="text-sm dark:text-gray-300" data-id="element-284">
                          Total Balance Value
                        </span>
                        {showWalletBalance ? <span className="text-lg font-bold dark:text-white" data-id="element-285">
                            $78.45 USD
                          </span> : <span className="text-lg font-bold dark:text-white" data-id="element-286">
                            ••••••
                          </span>}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-2" data-id="element-287">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-100 dark:border-gray-600" data-id="element-288">
                          <div className="flex items-center gap-1 mb-1" data-id="element-289">
                            <span className="text-pink-500 dark:text-pink-400 text-sm" data-id="element-290">
                              💗
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-291">
                              LuvTokens
                            </span>
                          </div>
                          {showWalletBalance ? <span className="text-lg font-bold dark:text-white" data-id="element-292">
                              240
                            </span> : <span className="text-lg font-bold dark:text-white" data-id="element-293">
                              ••••
                            </span>}
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-100 dark:border-gray-600" data-id="element-294">
                          <div className="flex items-center gap-1 mb-1" data-id="element-295">
                            <span className="text-purple-500 dark:text-purple-400 text-sm" data-id="element-296">
                              💎
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-297">
                              DesireGems
                            </span>
                          </div>
                          {showWalletBalance ? <span className="text-lg font-bold dark:text-white" data-id="element-298">
                              15
                            </span> : <span className="text-lg font-bold dark:text-white" data-id="element-299">
                              ••
                            </span>}
                        </div>
                      </div>
                      <div className="flex gap-2" data-id="element-300">
                        <button className="flex-1 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm flex items-center justify-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-300 transition-colors" data-id="element-301">
                          <Download size={14} data-id="element-302" />
                          <span data-id="element-303">Receive</span>
                        </button>
                        <button className="flex-1 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm flex items-center justify-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-300 transition-colors" data-id="element-304">
                          <Upload size={14} data-id="element-305" />
                          <span data-id="element-306">Send</span>
                        </button>
                        <button className="flex-1 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm flex items-center justify-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-300 transition-colors" data-id="element-307">
                          <ArrowLeftRight size={14} data-id="element-308" />
                          <span data-id="element-309">Convert</span>
                        </button>
                      </div>
                    </div>
                    {/* Quick Actions */}
                    <div className="grid grid-cols-4 gap-2" data-id="element-310">
                      <button className="p-2 bg-white dark:bg-gray-800 rounded-lg flex flex-col items-center gap-1 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-311">
                        <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-312">
                          <Plus size={14} data-id="element-313" />
                        </div>
                        <span className="text-xs dark:text-gray-300" data-id="element-314">Buy</span>
                      </button>
                      <button className="p-2 bg-white dark:bg-gray-800 rounded-lg flex flex-col items-center gap-1 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-315">
                        <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-316">
                          <Gift size={14} data-id="element-317" />
                        </div>
                        <span className="text-xs dark:text-gray-300" data-id="element-318">Gift</span>
                      </button>
                      <button className="p-2 bg-white dark:bg-gray-800 rounded-lg flex flex-col items-center gap-1 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-319">
                        <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-320">
                          <History size={14} data-id="element-321" />
                        </div>
                        <span className="text-xs dark:text-gray-300" data-id="element-322">
                          History
                        </span>
                      </button>
                      <button className="p-2 bg-white dark:bg-gray-800 rounded-lg flex flex-col items-center gap-1 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-323">
                        <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-324">
                          <Shield size={14} data-id="element-325" />
                        </div>
                        <span className="text-xs dark:text-gray-300" data-id="element-326">
                          Security
                        </span>
                      </button>
                    </div>
                  </div>
                  {/* Token Converter */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-327">
                    <h3 className="font-medium mb-3 dark:text-white" data-id="element-328">
                      Token Converter
                    </h3>
                    <div className="space-y-3" data-id="element-329">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600" data-id="element-330">
                        <div className="flex items-center justify-between mb-2" data-id="element-331">
                          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-332">
                            From
                          </span>
                          <div className="flex items-center gap-2" data-id="element-333">
                            <button className={`px-2 py-1 rounded-lg text-xs ${selectedCurrency === 'luv' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-500 dark:text-pink-400' : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`} onClick={() => setSelectedCurrency('luv')} data-id="element-334">
                              💗 LuvTokens
                            </button>
                            <button className={`px-2 py-1 rounded-lg text-xs ${selectedCurrency === 'gem' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`} onClick={() => setSelectedCurrency('gem')} data-id="element-335">
                              💎 DesireGems
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-336">
                          <input type="number" className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-lg dark:text-white" placeholder="0" data-id="element-337" />
                          <div className="text-lg font-bold dark:text-white" data-id="element-338">
                            {selectedCurrency === 'luv' ? '💗' : '💎'}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center" data-id="element-339">
                        <button className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" data-id="element-340">
                          <ArrowRight size={18} className="text-gray-500 dark:text-gray-400" data-id="element-341" />
                        </button>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600" data-id="element-342">
                        <div className="flex items-center justify-between mb-2" data-id="element-343">
                          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-344">
                            To
                          </span>
                          <div className="flex items-center gap-2" data-id="element-345">
                            <button className={`px-2 py-1 rounded-lg text-xs ${selectedCurrency === 'gem' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-500 dark:text-pink-400' : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`} data-id="element-346">
                              💗 LuvTokens
                            </button>
                            <button className={`px-2 py-1 rounded-lg text-xs ${selectedCurrency === 'luv' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`} data-id="element-347">
                              💎 DesireGems
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-348">
                          <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-lg text-gray-500 dark:text-gray-400" data-id="element-349">
                            {selectedCurrency === 'luv' ? '≈ 2' : '≈ 20'}
                          </div>
                          <div className="text-lg font-bold dark:text-white" data-id="element-350">
                            {selectedCurrency === 'luv' ? '💎' : '💗'}
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-xs text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-600" data-id="element-351">
                        <div className="flex items-center justify-between mb-1" data-id="element-352">
                          <span data-id="element-353">Exchange Rate</span>
                          <span data-id="element-354">
                            {selectedCurrency === 'luv' ? '10 💗 = 1 💎' : '1 💎 = 10 💗'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-355">
                          <span data-id="element-356">Conversion Fee</span>
                          <span className="text-yellow-500 dark:text-yellow-400" data-id="element-357">
                            5%
                          </span>
                        </div>
                      </div>
                      <button className="w-full py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white text-sm font-medium hover:shadow-md hover:shadow-pink-600/20 transition-all duration-300" data-id="element-358">
                        Convert Tokens
                      </button>
                    </div>
                  </div>
                  {/* Transaction History */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-359">
                    <div className="flex items-center justify-between mb-3" data-id="element-360">
                      <h3 className="font-medium dark:text-white" data-id="element-361">
                        Transaction History
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-362">
                        <div className="relative" data-id="element-363">
                          <input type="text" placeholder="Search..." className="bg-gray-50 dark:bg-gray-700 rounded-lg pl-8 pr-3 py-1 text-xs w-24 focus:w-32 transition-all border border-gray-200 dark:border-gray-600 dark:text-gray-300" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-364" />
                          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-365" />
                        </div>
                        <button className="p-1 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors" data-id="element-366">
                          <Filter size={14} className="text-gray-500 dark:text-gray-400" data-id="element-367" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide" data-id="element-368">
                      {['all', 'received', 'sent', 'converted', 'purchased'].map(type => <button key={type} className={`px-2 py-1 rounded-lg text-xs whitespace-nowrap ${filterType === type ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-500 dark:text-pink-400' : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} onClick={() => setFilterType(type)} data-id="element-369">
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>)}
                    </div>
                    <div className="space-y-3" data-id="element-370">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-371">
                        <div className="flex items-center justify-between mb-1" data-id="element-372">
                          <div className="flex items-center gap-2" data-id="element-373">
                            <div className="w-8 h-8 flex items-center justify-center bg-green-100 dark:bg-green-900/30 rounded-full text-green-500 dark:text-green-400" data-id="element-374">
                              <Download size={14} data-id="element-375" />
                            </div>
                            <div data-id="element-376">
                              <p className="text-sm font-medium dark:text-white" data-id="element-377">
                                Received
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-378">
                                From: Sophia
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-379">
                            <p className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-380">
                              +50 💗
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-381">
                              May 10, 2023
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-382">
                        <div className="flex items-center justify-between mb-1" data-id="element-383">
                          <div className="flex items-center gap-2" data-id="element-384">
                            <div className="w-8 h-8 flex items-center justify-center bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-500 dark:text-purple-400" data-id="element-385">
                              <CreditCard size={14} data-id="element-386" />
                            </div>
                            <div data-id="element-387">
                              <p className="text-sm font-medium dark:text-white" data-id="element-388">
                                Purchased
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-389">
                                Premium Pack
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-390">
                            <div className="flex items-center justify-end gap-1" data-id="element-391">
                              <p className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-392">
                                +100 💗
                              </p>
                              <p className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-393">
                                +10 💎
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-394">
                              May 8, 2023
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-395">
                        <div className="flex items-center justify-between mb-1" data-id="element-396">
                          <div className="flex items-center gap-2" data-id="element-397">
                            <div className="w-8 h-8 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full text-red-500 dark:text-red-400" data-id="element-398">
                              <Upload size={14} data-id="element-399" />
                            </div>
                            <div data-id="element-400">
                              <p className="text-sm font-medium dark:text-white" data-id="element-401">
                                Sent
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-402">
                                To: Kim Holland
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-403">
                            <p className="text-sm font-medium text-red-500 dark:text-red-400" data-id="element-404">
                              -25 💗
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-405">
                              May 5, 2023
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-406">
                        <div className="flex items-center justify-between mb-1" data-id="element-407">
                          <div className="flex items-center gap-2" data-id="element-408">
                            <div className="w-8 h-8 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-500 dark:text-blue-400" data-id="element-409">
                              <ArrowLeftRight size={14} data-id="element-410" />
                            </div>
                            <div data-id="element-411">
                              <p className="text-sm font-medium dark:text-white" data-id="element-412">
                                Converted
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-413">
                                LuvTokens to DesireGems
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-414">
                            <div className="flex flex-col items-end" data-id="element-415">
                              <p className="text-sm font-medium text-red-500 dark:text-red-400" data-id="element-416">
                                -50 💗
                              </p>
                              <p className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-417">
                                +4 💎
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-418">
                              May 3, 2023
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button className="w-full mt-3 py-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors" data-id="element-419">
                      View More
                    </button>
                  </div>
                  {/* Wallet Security */}
                  <div className="bg-gradient-to-r from-blue-50 to-gray-50 dark:from-blue-900/20 dark:to-gray-800/50 rounded-xl border border-blue-200 dark:border-blue-800/30 p-4 shadow-sm hover:shadow-lg hover:shadow-blue-600/10 transition-all duration-300" data-id="element-420">
                    <div className="flex items-center gap-2 mb-3" data-id="element-421">
                      <Shield size={18} className="text-blue-500 dark:text-blue-400" data-id="element-422" />
                      <h3 className="font-medium dark:text-white" data-id="element-423">
                        Wallet Security
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-424">
                      <div className="flex items-center justify-between" data-id="element-425">
                        <div className="flex items-center gap-2" data-id="element-426">
                          <Lock size={16} className="text-gray-500 dark:text-gray-400" data-id="element-427" />
                          <span className="text-sm dark:text-gray-300" data-id="element-428">
                            Transaction PIN
                          </span>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-429">
                          <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-430"></div>
                          <span className="text-xs text-green-500 dark:text-green-400" data-id="element-431">
                            Enabled
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-432">
                        <div className="flex items-center gap-2" data-id="element-433">
                          <AlertTriangle size={16} className="text-gray-500 dark:text-gray-400" data-id="element-434" />
                          <span className="text-sm dark:text-gray-300" data-id="element-435">
                            Spending Limits
                          </span>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-436">
                          <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-437"></div>
                          <span className="text-xs text-red-500 dark:text-red-400" data-id="element-438">
                            Disabled
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-439">
                        <div className="flex items-center gap-2" data-id="element-440">
                          <Eye size={16} className="text-gray-500 dark:text-gray-400" data-id="element-441" />
                          <span className="text-sm dark:text-gray-300" data-id="element-442">
                            Hide Balance
                          </span>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-443">
                          <div className="w-2 h-2 rounded-full bg-gray-500" data-id="element-444"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-445">
                            Optional
                          </span>
                        </div>
                      </div>
                      <button className="w-full py-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-sm text-blue-500 dark:text-blue-400 mt-2 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors" data-id="element-446">
                        Manage Security Settings
                      </button>
                    </div>
                  </div>
                </div>}
              {activeTab === 'staking' && <div className="space-y-4" data-id="element-447">
                  <div className="bg-gradient-to-r from-purple-50 to-gray-50 dark:from-purple-900/20 dark:to-gray-800/50 rounded-xl border border-purple-200 dark:border-purple-800/30 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/10 transition-all duration-300" data-id="element-448">
                    <h3 className="font-medium mb-2 dark:text-white" data-id="element-449">
                      Token Staking
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-450">
                      Lock your tokens to earn interest and exclusive benefits
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-451">
                      <div className="flex items-center justify-between mb-2" data-id="element-452">
                        <span className="text-sm dark:text-gray-300" data-id="element-453">
                          Currently Staked
                        </span>
                        <span className="text-sm font-medium dark:text-white" data-id="element-454">
                          100 💗
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-455">
                        <span className="text-sm dark:text-gray-300" data-id="element-456">
                          Staking Period
                        </span>
                        <span className="text-sm font-medium dark:text-white" data-id="element-457">
                          30 days
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-458">
                        <span className="text-sm dark:text-gray-300" data-id="element-459">
                          Interest Rate
                        </span>
                        <span className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-460">
                          +15%
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-461">
                        <span className="text-sm dark:text-gray-300" data-id="element-462">
                          Expected Return
                        </span>
                        <span className="text-sm font-medium text-green-500 dark:text-green-400" data-id="element-463">
                          115 💗
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2 mb-4" data-id="element-464">
                      <div className="flex items-center justify-between" data-id="element-465">
                        <span className="text-sm dark:text-gray-300" data-id="element-466">
                          Time Remaining
                        </span>
                        <div className="flex items-center gap-1" data-id="element-467">
                          <Clock size={14} className="text-gray-500 dark:text-gray-400" data-id="element-468" />
                          <span className="text-sm dark:text-gray-300" data-id="element-469">
                            18 days
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-470">
                        <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 w-2/5 rounded-full" data-id="element-471"></div>
                      </div>
                    </div>
                    <div className="flex justify-between" data-id="element-472">
                      <button className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 dark:text-gray-300 transition-colors" data-id="element-473">
                        Stake More
                      </button>
                      <button className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-colors" data-id="element-474">
                        Unstake Early (-5%)
                      </button>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-475">
                    <h3 className="font-medium mb-3 dark:text-white" data-id="element-476">
                      Staking Plans
                    </h3>
                    <div className="space-y-3" data-id="element-477">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex justify-between items-center hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-478">
                        <div data-id="element-479">
                          <div className="flex items-center gap-2" data-id="element-480">
                            <span className="text-sm font-medium dark:text-white" data-id="element-481">
                              Quick Stake
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-482">
                              7 Days
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-483">
                            5% interest rate
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 rounded-full text-xs text-pink-500 dark:text-pink-400 hover:bg-pink-200 dark:hover:bg-pink-900/50 transition-colors" data-id="element-484">
                          Stake Now
                        </button>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex justify-between items-center hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-485">
                        <div data-id="element-486">
                          <div className="flex items-center gap-2" data-id="element-487">
                            <span className="text-sm font-medium dark:text-white" data-id="element-488">
                              Standard Stake
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-489">
                              30 Days
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-490">
                            15% interest rate
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 rounded-full text-xs text-pink-500 dark:text-pink-400 hover:bg-pink-200 dark:hover:bg-pink-900/50 transition-colors" data-id="element-491">
                          Stake Now
                        </button>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 rounded-lg border border-purple-200 dark:border-purple-800/30 flex justify-between items-center hover:shadow-md hover:shadow-purple-600/10 transition-all duration-300" data-id="element-492">
                        <div data-id="element-493">
                          <div className="flex items-center gap-2" data-id="element-494">
                            <span className="text-sm font-medium dark:text-white" data-id="element-495">
                              Premium Stake
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-500 dark:text-purple-400" data-id="element-496">
                              90 Days
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-497">
                            40% interest rate + exclusive content
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-498">
                          Stake Now
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-499">
                    <div className="flex items-center gap-2 mb-2" data-id="element-500">
                      <Star size={16} className="text-yellow-500 dark:text-yellow-400" data-id="element-501" />
                      <h3 className="font-medium dark:text-white" data-id="element-502">
                        Staking Benefits
                      </h3>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-503">
                      <p data-id="element-504">• Earn interest on your tokens</p>
                      <p data-id="element-505">• Unlock exclusive content while staking</p>
                      <p data-id="element-506">• Receive staking badges on your profile</p>
                      <p data-id="element-507">• Priority access to limited events</p>
                    </div>
                  </div>
                </div>}
              {activeTab === 'subscription' && <div className="space-y-4" data-id="element-508">
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl border border-pink-200 dark:border-pink-800/30 p-4 shadow-sm hover:shadow-lg hover:shadow-pink-600/10 transition-all duration-300" data-id="element-509">
                    <div className="flex items-center gap-2 mb-3" data-id="element-510">
                      <RefreshCcw size={18} className="text-pink-500 dark:text-pink-400" data-id="element-511" />
                      <h3 className="font-medium dark:text-white" data-id="element-512">
                        Token Subscription
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-513">
                      Receive tokens automatically every month at a discounted
                      rate
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-514">
                      <div className="flex items-center justify-between mb-2" data-id="element-515">
                        <span className="text-sm dark:text-gray-300" data-id="element-516">
                          Current Plan
                        </span>
                        <div className="text-xs px-2 py-0.5 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-517">
                          Silver
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-518">
                        <span className="text-sm dark:text-gray-300" data-id="element-519">
                          Monthly Tokens
                        </span>
                        <div className="flex items-center gap-2" data-id="element-520">
                          <span className="text-sm font-medium dark:text-white" data-id="element-521">
                            100 💗
                          </span>
                          <span className="text-sm font-medium dark:text-white" data-id="element-522">
                            10 💎
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-523">
                        <span className="text-sm dark:text-gray-300" data-id="element-524">
                          Next Delivery
                        </span>
                        <div className="flex items-center gap-1" data-id="element-525">
                          <Calendar size={14} className="text-gray-500 dark:text-gray-400" data-id="element-526" />
                          <span className="text-sm dark:text-gray-300" data-id="element-527">
                            May 15, 2023
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-528">
                        <span className="text-sm dark:text-gray-300" data-id="element-529">
                          Monthly Cost
                        </span>
                        <span className="text-sm font-medium dark:text-white" data-id="element-530">
                          $9.99
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between" data-id="element-531">
                      <button className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 dark:text-gray-300 transition-colors" data-id="element-532">
                        Change Plan
                      </button>
                      <button className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-sm text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-533">
                        Manage Subscription
                      </button>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-534">
                    <h3 className="font-medium mb-3 dark:text-white" data-id="element-535">
                      Available Plans
                    </h3>
                    <div className="space-y-3" data-id="element-536">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-537">
                        <div className="flex items-center justify-between mb-2" data-id="element-538">
                          <div className="flex items-center gap-2" data-id="element-539">
                            <span className="text-sm font-medium dark:text-white" data-id="element-540">
                              Bronze Plan
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-full text-yellow-600 dark:text-yellow-400" data-id="element-541">
                              Basic
                            </div>
                          </div>
                          <span className="text-sm font-medium dark:text-white" data-id="element-542">
                            $4.99/mo
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-543">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-544">
                            Monthly Tokens
                          </span>
                          <div className="flex items-center gap-2" data-id="element-545">
                            <span className="text-xs dark:text-gray-300" data-id="element-546">
                              50 💗
                            </span>
                            <span className="text-xs dark:text-gray-300" data-id="element-547">
                              5 💎
                            </span>
                          </div>
                        </div>
                        <button className="w-full py-1.5 bg-gray-100 dark:bg-gray-600 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-500 border border-gray-200 dark:border-gray-600 dark:text-gray-300 transition-colors" data-id="element-548">
                          Select Plan
                        </button>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-pink-50 to-white dark:from-pink-900/20 dark:to-gray-800 rounded-lg border border-pink-200 dark:border-pink-800/30 hover:shadow-md hover:shadow-pink-600/10 transition-all duration-300" data-id="element-549">
                        <div className="flex items-center justify-between mb-2" data-id="element-550">
                          <div className="flex items-center gap-2" data-id="element-551">
                            <span className="text-sm font-medium dark:text-white" data-id="element-552">
                              Silver Plan
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-553">
                              Current
                            </div>
                          </div>
                          <span className="text-sm font-medium dark:text-white" data-id="element-554">
                            $9.99/mo
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-555">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-556">
                            Monthly Tokens
                          </span>
                          <div className="flex items-center gap-2" data-id="element-557">
                            <span className="text-xs dark:text-gray-300" data-id="element-558">
                              100 💗
                            </span>
                            <span className="text-xs dark:text-gray-300" data-id="element-559">
                              10 💎
                            </span>
                          </div>
                        </div>
                        <button className="w-full py-1.5 bg-pink-100 dark:bg-pink-900/30 rounded-lg text-xs text-pink-500 dark:text-pink-400 transition-colors" data-id="element-560">
                          Current Plan
                        </button>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 rounded-lg border border-purple-200 dark:border-purple-800/30 hover:shadow-md hover:shadow-purple-600/10 transition-all duration-300" data-id="element-561">
                        <div className="absolute right-6 -mt-6 px-2 py-0.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white shadow-md shadow-purple-600/20" data-id="element-562">
                          Best Value
                        </div>
                        <div className="flex items-center justify-between mb-2" data-id="element-563">
                          <div className="flex items-center gap-2" data-id="element-564">
                            <span className="text-sm font-medium dark:text-white" data-id="element-565">
                              Gold Plan
                            </span>
                            <div className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-full text-yellow-600 dark:text-yellow-400" data-id="element-566">
                              Premium
                            </div>
                          </div>
                          <span className="text-sm font-medium dark:text-white" data-id="element-567">
                            $19.99/mo
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-568">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-569">
                            Monthly Tokens
                          </span>
                          <div className="flex items-center gap-2" data-id="element-570">
                            <span className="text-xs dark:text-gray-300" data-id="element-571">
                              250 💗
                            </span>
                            <span className="text-xs dark:text-gray-300" data-id="element-572">
                              30 💎
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3" data-id="element-573">
                          <Star size={12} className="text-yellow-500 dark:text-yellow-400" data-id="element-574" />
                          <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-575">
                            Includes VIP benefits & exclusive content
                          </span>
                        </div>
                        <button className="w-full py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-576">
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'rewards' && <div className="space-y-4" data-id="element-577">
                  <div className="bg-gradient-to-r from-yellow-50 to-gray-50 dark:from-yellow-900/20 dark:to-gray-800/50 rounded-xl border border-yellow-200 dark:border-yellow-800/30 p-4 shadow-sm hover:shadow-lg hover:shadow-yellow-600/10 transition-all duration-300" data-id="element-578">
                    <div className="flex items-center gap-2 mb-3" data-id="element-579">
                      <Award size={18} className="text-yellow-500 dark:text-yellow-400" data-id="element-580" />
                      <h3 className="font-medium dark:text-white" data-id="element-581">
                        Token Rewards Program
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-582">
                      Earn rewards based on your activity and token usage
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-583">
                      <div className="flex items-center justify-between mb-2" data-id="element-584">
                        <span className="text-sm dark:text-gray-300" data-id="element-585">
                          Rewards Level
                        </span>
                        <div className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-full text-yellow-600 dark:text-yellow-400" data-id="element-586">
                          Gold
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-587">
                        <span className="text-sm dark:text-gray-300" data-id="element-588">
                          Points Balance
                        </span>
                        <span className="text-sm font-medium dark:text-white" data-id="element-589">
                          3,450 pts
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2" data-id="element-590">
                        <span className="text-sm dark:text-gray-300" data-id="element-591">
                          Next Reward
                        </span>
                        <span className="text-sm font-medium dark:text-white" data-id="element-592">
                          4,000 pts (50 💗)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-2" data-id="element-593">
                        <div className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 w-[86%] rounded-full" data-id="element-594"></div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-1" data-id="element-595">
                        550 points to next reward
                      </p>
                    </div>
                    <div className="flex justify-between" data-id="element-596">
                      <button className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 dark:text-gray-300 transition-colors" data-id="element-597">
                        Points History
                      </button>
                      <button className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-lg text-sm text-white hover:shadow-md hover:shadow-yellow-500/20 transition-all duration-300" data-id="element-598">
                        Redeem Rewards
                      </button>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-599">
                    <h3 className="font-medium mb-3 dark:text-white" data-id="element-600">
                      Available Rewards
                    </h3>
                    <div className="space-y-3" data-id="element-601">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex justify-between items-center hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-602">
                        <div data-id="element-603">
                          <p className="text-sm font-medium dark:text-white" data-id="element-604">
                            10 DesireGems
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-605">
                            Premium currency
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-606">
                          2,000 pts
                        </button>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex justify-between items-center hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-607">
                        <div data-id="element-608">
                          <p className="text-sm font-medium dark:text-white" data-id="element-609">
                            Exclusive Avatar Frame
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-610">
                            Gold tier profile decoration
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-611">
                          3,500 pts
                        </button>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex justify-between items-center hover:border-pink-300 dark:hover:border-pink-800/50 transition-all duration-300" data-id="element-612">
                        <div data-id="element-613">
                          <p className="text-sm font-medium dark:text-white" data-id="element-614">
                            Premium Content Unlock
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-615">
                            Any premium photo or video
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-616">
                          4,000 pts
                        </button>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 rounded-lg border border-purple-200 dark:border-purple-800/30 flex justify-between items-center hover:shadow-md hover:shadow-purple-600/10 transition-all duration-300" data-id="element-617">
                        <div data-id="element-618">
                          <div className="flex items-center gap-2" data-id="element-619">
                            <p className="text-sm font-medium dark:text-white" data-id="element-620">
                              VIP Experience
                            </p>
                            <div className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-500 dark:text-purple-400" data-id="element-621">
                              Special
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-622">
                            1 month of VIP status + 100 💎
                          </p>
                        </div>
                        <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white hover:shadow-md hover:shadow-purple-600/20 transition-all duration-300" data-id="element-623">
                          10,000 pts
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-lg hover:shadow-purple-600/5 transition-all duration-300" data-id="element-624">
                    <h3 className="font-medium mb-3 dark:text-white" data-id="element-625">
                      How to Earn Points
                    </h3>
                    <div className="space-y-2" data-id="element-626">
                      <div className="flex items-center justify-between" data-id="element-627">
                        <div className="flex items-center gap-2" data-id="element-628">
                          <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-629">
                            <MessageCircle size={14} data-id="element-630" />
                          </div>
                          <span className="text-sm dark:text-gray-300" data-id="element-631">
                            Daily Login
                          </span>
                        </div>
                        <span className="text-sm dark:text-gray-300" data-id="element-632">
                          +50 pts
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-633">
                        <div className="flex items-center gap-2" data-id="element-634">
                          <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-635">
                            <Heart size={14} data-id="element-636" />
                          </div>
                          <span className="text-sm dark:text-gray-300" data-id="element-637">
                            React to AI Posts
                          </span>
                        </div>
                        <span className="text-sm dark:text-gray-300" data-id="element-638">
                          +10 pts each
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-639">
                        <div className="flex items-center gap-2" data-id="element-640">
                          <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-641">
                            <Gift size={14} data-id="element-642" />
                          </div>
                          <span className="text-sm dark:text-gray-300" data-id="element-643">
                            Send Gifts
                          </span>
                        </div>
                        <span className="text-sm dark:text-gray-300" data-id="element-644">
                          +100 pts
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-645">
                        <div className="flex items-center gap-2" data-id="element-646">
                          <div className="w-8 h-8 flex items-center justify-center bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-500 dark:text-pink-400" data-id="element-647">
                            <CreditCard size={14} data-id="element-648" />
                          </div>
                          <span className="text-sm dark:text-gray-300" data-id="element-649">
                            Token Purchases
                          </span>
                        </div>
                        <span className="text-sm dark:text-gray-300" data-id="element-650">
                          +1 pt per 💗
                        </span>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      <style jsx data-id="element-651">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>;
};